import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useSettingsContext } from 'src/components/settings';
import { HEADER, NAV } from 'src/layouts/config-layout';

// ----------------------------------------------------------------------
// "Background" header layout.
//
// The theme image is a FIXED background pinned to the top of the viewport,
// sitting behind the menu header (which is translucent) and the page content.
// The first card starts right below the image band, and as the user scrolls,
// the (opaque) cards slide up and cover the image.
//
// When the theme provides `aspectRatio` (width / height), the image is
// expanded to the FULL content width keeping its proportion (no crop) — the
// band height then follows from the width. Otherwise it falls back to a fixed
// `height` band (cover/contain via `fit`).
//
// Stacking: the fixed image sits at zIndex -1, so it paints above the page's
// base background but below ALL in-flow content (filters bar, cards, sidebar)
// and below the menu header (which has a high zIndex and shows the image
// blurred through its translucent background). No per-element zIndex needed on
// the content for it to cover the image while scrolling.
// ----------------------------------------------------------------------

// Spacing added by <Main> on top of the header height.
const MAIN_SPACING = 8;

// When the image fits, pull the content up a little so a sliver of the first
// card peeks above the fold — a hint that there's more to scroll.
const PEEK = { xs: 72, md: 112 };

// Guaranteed peek: the content must always start at least this many px above
// the bottom of the viewport, even when the image is taller than the screen
// (full-width-proportional can exceed the viewport height). Caps the spacer.
const MIN_CARD_PEEK = { xs: 150, md: 210 };

export default function ThemeBackgroundHeader({
  image,
  fit,
  title,
  height = { xs: 220, md: 420 },
  aspectRatio,
}) {
  const settings = useSettingsContext();
  const isHorizontal = settings.themeLayout === 'horizontal';
  const isMini = settings.themeLayout === 'mini';

  // The menu header / main content area is offset by the sidebar on lg+.
  // eslint-disable-next-line no-nested-ternary
  const leftOffset = isHorizontal ? 0 : isMini ? NAV.W_MINI : NAV.W_VERTICAL;

  // <Main> top padding, so the spacer ends exactly at the image's bottom edge.
  const padXs = HEADER.H_MOBILE + MAIN_SPACING;
  const padMd = HEADER.H_DESKTOP + MAIN_SPACING;

  // Full-width-proportional mode: derive the band height from the viewport
  // width and the image ratio, so the whole image shows full width, uncropped.
  const proportional = Number.isFinite(aspectRatio) && aspectRatio > 0;

  const imageSx = proportional
    ? {
        aspectRatio: String(aspectRatio),
        backgroundSize: 'cover',
      }
    : {
        height,
        backgroundSize: fit === 'contain' ? 'contain' : 'cover',
      };

  const spacerHeight = proportional
    ? {
        xs: `min(calc(100vw / ${aspectRatio} - ${padXs + PEEK.xs}px), calc(100vh - ${
          padXs + MIN_CARD_PEEK.xs
        }px))`,
        lg: `min(calc((100vw - ${leftOffset}px) / ${aspectRatio} - ${
          padMd + PEEK.md
        }px), calc(100vh - ${padMd + MIN_CARD_PEEK.md}px))`,
      }
    : {
        xs: Math.max((typeof height === 'number' ? height : height.xs) - padXs - PEEK.xs, 0),
        md: Math.max(
          (typeof height === 'number' ? height : height.md ?? height.xs) - padMd - PEEK.md,
          0
        ),
      };

  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          left: { xs: 0, lg: `${leftOffset}px` },
          zIndex: -1,
          pointerEvents: 'none',
          backgroundImage: `url(${image})`,
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          ...imageSx,
        }}
      />

      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            height: spacerHeight,
            display: 'flex',
            alignItems: 'flex-end',
            mb: { xs: 3, md: 5 },
            pointerEvents: 'none',
          }}
        >
          {title && (
            <Typography
              variant="h4"
              sx={{ color: 'common.white', textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}
            >
              {title}
            </Typography>
          )}
        </Box>
      </Container>
    </>
  );
}

ThemeBackgroundHeader.propTypes = {
  image: PropTypes.string,
  fit: PropTypes.oneOf(['contain', 'cover']),
  title: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  aspectRatio: PropTypes.number,
};
